
import React, { useState, useMemo, useRef } from 'react';
import { ImdsStandard, StandardLimit, RegulatoryRow, ProcessedMds, AnalysisResult } from '../types';
import * as dbService from '../services/firebaseService';
import * as XLSX from 'xlsx';

interface ImdsStandardPageProps {
  savedFiles: ProcessedMds[];
  standards: ImdsStandard[];
  onSaveStandards: (standards: ImdsStandard[]) => Promise<void>;
  onViewDrawing?: (dwgNumber: string) => void;
}

const ImdsStandardPage: React.FC<ImdsStandardPageProps> = ({ savedFiles, standards, onSaveStandards, onViewDrawing }) => {
  const [activeTab, setActiveTab] = useState<'master' | 'library' | 'analysis' | 'results'>('master');
  const [selectedTarget, setSelectedTarget] = useState<string>('');
  const [selectedStandards, setSelectedStandards] = useState<string[]>([]);
  
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isProcessingExcel, setIsProcessingExcel] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [results, setResults] = useState<AnalysisResult[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [gadslSearch, setGadslSearch] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [newStd, setNewStd] = useState<Partial<ImdsStandard>>({
    standard_id: '',
    standardName: '',
    ownerCompany: '',
    type: 'REGULATORY',
    status: 'ACTIVE',
    limits: [],
    regulatoryRows: [],
    technicalNotes: ''
  });

  const [expandedStdId, setExpandedStdId] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<Record<string, number[]>>({});
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const filteredGadslRows = useMemo(() => {
    if (!newStd.regulatoryRows) return [];
    const term = gadslSearch.toLowerCase();
    const cleanTerm = term.replace(/-/g, ''); // For robust CAS number matching without hyphens
    
    return newStd.regulatoryRows.filter(row => {
      const substanceMatch = row.substance?.toLowerCase().includes(term);
      const casMatch = row.casNumber?.toLowerCase().includes(term) || 
                       row.casNumber?.replace(/-/g, '').includes(cleanTerm);
      const criteriaMatch = row.hondaCriteria?.toLowerCase().includes(term);
      const lawsMatch = row.relevantLaws?.toLowerCase().includes(term);
      
      return substanceMatch || casMatch || criteriaMatch || lawsMatch;
    });
  }, [newStd.regulatoryRows, gadslSearch]);

  const [dynamicPreview, setDynamicPreview] = useState<{ headers: string[], rows: any[][] } | null>(null);

  const handleExcelUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsProcessingExcel(true);
    setUploadProgress(0);

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 }) as any[][];

        if (data.length > 0) {
            const headers = data[0].map(h => String(h));
            const rows = data.slice(1);
            setDynamicPreview({ headers, rows });
        }

        const rawRows = data.slice(1);

        // Heuristic to detect CAS column index
        // Default GADSL: Col 1 = Substance, Col 2 = CAS
        let casColIndex = 2; 
        let substanceColIndex = 1; 
        
        // Check first 10 rows to detect format
        let casInCol1Count = 0;
        const casRegex = /\b\d{2,7}-\d{2}-\d\b/;
        
        for (let i = 0; i < Math.min(rawRows.length, 10); i++) {
            const r = rawRows[i];
            if (r[1] && casRegex.test(String(r[1]).trim())) casInCol1Count++;
        }
        
        // If Column 1 looks like CAS, swap mapping
        if (casInCol1Count > 2) { // Threshold > 2 to be safe
            casColIndex = 1;
            substanceColIndex = 2;
        }

        const processedRows: RegulatoryRow[] = rawRows.filter(r => r[0] || r[1]).map(row => ({
          gadslNo: String(row[0] || '-'),
          substance: String(row[substanceColIndex] || ''),
          casNumber: String(row[casColIndex] || '-'),
          hondaApplication: String(row[3] || '-'),
          imdsAppId: String(row[4] || '-'),
          hondaCriteria: String(row[5] || '-'),
          mainApplications: String(row[6] || '-'),
          relevantLaws: String(row[7] || '-')
        }));

        setNewStd(prev => ({ 
          ...prev, 
          regulatoryRows: processedRows,
          standardName: prev.standardName || `IMPORTED-STD-${new Date().getFullYear()}`,
          ownerCompany: prev.ownerCompany || "External Source"
        }));
        setIsProcessingExcel(false);
      } catch (error) {
        alert('Gagal memproses Excel.');
        setIsProcessingExcel(false);
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleSaveStandard = async () => {
    if (!newStd.standardName || !newStd.ownerCompany) {
      alert('Data tidak lengkap.');
      return;
    }
    setIsSyncing(true);
    try {
      const fullStd: ImdsStandard = {
        ...newStd as ImdsStandard,
        id: editingId || `STD-${Date.now()}`,
        version: '1.0',
        effectiveDate: new Date().toISOString().split('T')[0],
      };
      const updated = editingId ? standards.map(s => s.id === editingId ? fullStd : s) : [...standards, fullStd];
      await onSaveStandards(updated);
      
      alert(editingId ? "Standard updated successfully." : "New standard committed to vault.");
      
      setEditingId(null);
      setActiveTab('library');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleLoadFromLibrary = (std: ImdsStandard) => {
    setEditingId(std.id);
    setNewStd({
      ...std,
      regulatoryRows: [...(std.regulatoryRows || [])]
    });
    setActiveTab('master');
    setGadslSearch('');
  };

  const handleResetForm = () => {
    setEditingId(null);
    setDynamicPreview(null);
    setNewStd({
      standard_id: '',
      standardName: '',
      ownerCompany: '',
      type: 'REGULATORY',
      status: 'ACTIVE',
      limits: [],
      regulatoryRows: [],
      technicalNotes: ''
    });
    setGadslSearch('');
  };

  const executeAnalysis = () => {
    if (!selectedTarget || selectedStandards.length === 0) return;
    setIsAnalyzing(true);
    
    setTimeout(() => {
      const targetDataset = savedFiles.find(f => f.dwg.dwg_number === selectedTarget);
      if (!targetDataset) { setIsAnalyzing(false); return; }

      const activeStds = standards.filter(s => selectedStandards.includes(s.id));
      const auditLog: AnalysisResult[] = [];

      targetDataset.items.forEach(item => {
        const matrix = item.details?.rohsComposition || {};
        activeStds.forEach(std => {
          // Filter rows if specific ones are selected for this standard
          const rowsToAudit = (selectedRows[std.id] && selectedRows[std.id].length > 0)
            ? std.regulatoryRows?.filter((_, idx) => selectedRows[std.id].includes(idx))
            : std.regulatoryRows;

          rowsToAudit?.forEach(reg => {
            const cleanCas = reg.casNumber.replace(/[^\d]/g, '');
            const matrixKey = matrix[cleanCas] !== undefined ? cleanCas : reg.substance.toLowerCase().replace(/\s+/g, '_');
            const matrixValStr = matrix[matrixKey];

            if (matrixValStr !== undefined) {
              const matrixVal = parseFloat(matrixValStr || '0');
              let threshold = 1000;
              const criteria = reg.hondaCriteria.toLowerCase();
              if (criteria.includes('prohibited')) threshold = 0;
              else if (criteria.includes('declarable')) threshold = 1000;

              auditLog.push({
                id: Math.random().toString(),
                partNumber: item.part_number_customer,
                partName: item.part_name_customer,
                standardName: std.standardName,
                ownerCompany: std.ownerCompany,
                substance: reg.substance,
                actualValue: matrixVal,
                limitValue: threshold,
                unit: 'PPM',
                status: matrixVal > threshold ? 'EXCEED' : 'COMPLY',
                dwgReference: targetDataset.dwg.dwg_number,
                regulatoryInfo: reg.relevantLaws,
                timestamp: new Date().toISOString()
              });
            }
          });
        });
      });

      setResults(auditLog);
      setIsAnalyzing(false);
      setActiveTab('results');
    }, 1200);
  };

  const handleDeleteStandard = async (id: string) => {
    setIsSyncing(true);
    try {
        await dbService.firebase_deleteStandard(id);
        const updated = standards.filter(s => s.id !== id);
        await onSaveStandards(updated); // This updates parent state
        setDeleteConfirmId(null);
    } catch (e) {
        console.error("Delete failed", e);
    } finally {
        setIsSyncing(false);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      {/* HEADER */}
      <div className="flex justify-between items-end border-b border-slate-200 dark:border-slate-700 pb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">IMDS & Regulatory</h2>
          <p className="text-sm text-slate-500 mt-1">Industrial Compliance Standard Engine</p>
        </div>
        <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
          {['master', 'library', 'analysis', 'results'].map(tab => (
            <button 
              key={tab} 
              onClick={() => setActiveTab(tab as any)} 
              className={`px-4 py-2 text-xs font-medium uppercase rounded-md transition-all ${activeTab === tab ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'master' && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* SIDE FORM */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 rounded-xl shadow-sm">
              <div className="flex justify-between items-center mb-6">
                 <h3 className="text-sm font-bold uppercase text-slate-900 dark:text-white flex items-center gap-2">
                   <div className="w-1 h-4 bg-blue-600 rounded-full"></div> 
                   {editingId ? 'Edit Standard' : 'New Standard'}
                 </h3>
                 {editingId && (
                   <button onClick={handleResetForm} className="text-xs font-medium text-blue-600 hover:text-blue-700">Reset</button>
                 )}
              </div>
              
              <div className="space-y-4">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-500 uppercase">Friendly Name</label>
                  <input className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all" placeholder="Standard Name" value={newStd.standardName} onChange={e => setNewStd({...newStd, standardName: e.target.value})} />
                </div>
                
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-500 uppercase">Authority/Agency</label>
                  <input className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all" placeholder="Owner Company" value={newStd.ownerCompany} onChange={e => setNewStd({...newStd, ownerCompany: e.target.value})} />
                </div>

                <div className="pt-2 border-t border-slate-100 dark:border-slate-700">
                  <label className="text-xs font-medium text-slate-500 uppercase mb-2 block">Source Data</label>
                  {!editingId ? (
                    <button onClick={() => fileInputRef.current?.click()} className="w-full py-3 bg-blue-50 dark:bg-blue-900/20 border border-dashed border-blue-300 dark:border-blue-700 rounded-lg text-xs font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-all flex items-center justify-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                      {isProcessingExcel ? 'Processing...' : 'Upload Excel File'}
                    </button>
                  ) : (
                    <div className="text-xs text-slate-500 italic bg-slate-50 dark:bg-slate-700/50 p-3 rounded border border-slate-200 dark:border-slate-600">
                        Editing existing standard. <button onClick={handleResetForm} className="text-blue-600 dark:text-blue-400 underline font-medium">Cancel edit</button> to upload new.
                    </div>
                  )}
                </div>
                
                <input type="file" ref={fileInputRef} className="hidden" onChange={handleExcelUpload} />
                
                <button onClick={handleSaveStandard} className={`w-full py-3 text-white font-medium text-sm rounded-lg shadow-sm transition-colors ${editingId ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-900 hover:bg-slate-800 dark:bg-blue-600 dark:hover:bg-blue-700'}`}>
                  {isSyncing ? 'Processing Sync...' : editingId ? 'Update Standard' : 'Save to Vault'}
                </button>
              </div>
            </div>
            
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl">
               <h4 className="text-xs font-bold text-blue-700 dark:text-blue-400 uppercase mb-2">Technical Guidance</h4>
               <p className="text-xs text-blue-600 dark:text-blue-300 leading-relaxed">Upload file Excel standar regulasi. Sistem akan memetakan kolom secara otomatis sesuai format yang diunggah.</p>
            </div>
          </div>

          {/* MAIN PREVIEW TABLE */}
          <div className="lg:col-span-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm overflow-hidden flex flex-col">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/50">
               <div className="flex items-center gap-3">
                 <span className="text-xs font-bold text-slate-500 uppercase">Registry Preview</span>
                 <span className="bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 px-2 py-0.5 text-[10px] font-bold rounded-full">{newStd.regulatoryRows?.length || 0} ITEMS</span>
               </div>
               <input type="text" placeholder="Search Substance or CAS..." className="px-3 py-1.5 border border-slate-200 dark:border-slate-600 rounded-lg text-xs bg-white dark:bg-slate-700 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 w-64" value={gadslSearch} onChange={e => setGadslSearch(e.target.value)} />
            </div>
            
            <div className="flex-1 overflow-auto max-h-[600px]">
              <table className="w-full text-left text-xs border-collapse table-fixed min-w-[1800px]">
                <thead className="sticky top-0 bg-slate-50 dark:bg-slate-700/80 border-b border-slate-200 dark:border-slate-700 text-slate-500 font-medium uppercase z-10 backdrop-blur-sm">
                  <tr className="divide-x divide-slate-200 dark:divide-slate-700">
                    {dynamicPreview ? (
                        dynamicPreview.headers.map((h, i) => (
                            <th key={i} className="p-3 min-w-[150px]">{h}</th>
                        ))
                    ) : (
                        <>
                            <th className="p-3 w-24 text-center">Ref No.</th>
                            <th className="p-3 w-64">Substance</th>
                            <th className="p-3 w-40 text-center">CAS Number</th>
                            <th className="p-3 w-60">Application</th>
                            <th className="p-3 w-32 text-center">App ID</th>
                            <th className="p-3 w-72">Criteria</th>
                            <th className="p-3 w-80">Usage</th>
                            <th className="p-3 w-80">Regulations</th>
                        </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {dynamicPreview ? (
                      dynamicPreview.rows
                        .filter(row => {
                           const term = gadslSearch.toLowerCase();
                           const cleanTerm = term.replace(/-/g, '');
                           return row.some(cell => {
                             const cellStr = String(cell || '').toLowerCase();
                             return cellStr.includes(term) || cellStr.replace(/-/g, '').includes(cleanTerm);
                           });
                        })
                        .map((row, i) => (
                          <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 divide-x divide-slate-100 dark:divide-slate-700 transition-colors">
                              {row.map((cell: any, j: number) => (
                                  <td key={j} className="p-3 text-slate-700 dark:text-slate-300 truncate" title={String(cell)}>
                                      {String(cell || '-')}
                                  </td>
                              ))}
                          </tr>
                        ))
                  ) : (
                      filteredGadslRows.length === 0 ? (
                        <tr><td colSpan={8} className="p-20 text-center text-slate-400 italic">No registry data loaded.</td></tr>
                      ) : (
                        filteredGadslRows.map((row, i) => (
                          <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 divide-x divide-slate-100 dark:divide-slate-700 transition-colors">
                            <td className="p-3 text-center text-slate-500 font-mono text-[10px]">{row.gadslNo}</td>
                            <td className="p-3 font-medium text-slate-900 dark:text-white">{row.substance}</td>
                            <td className="p-3 font-mono text-center text-slate-500 text-[10px]">{row.casNumber}</td>
                            <td className="p-3 text-[10px] text-slate-500 leading-tight">{row.hondaApplication}</td>
                            <td className="p-3 text-center font-mono text-slate-500 text-[10px]">{row.imdsAppId}</td>
                            <td className="p-3 text-[10px] font-medium text-slate-700 dark:text-slate-300 bg-yellow-50/50 dark:bg-yellow-900/10">{row.hondaCriteria}</td>
                            <td className="p-3 text-[10px] text-slate-400 leading-tight italic">{row.mainApplications}</td>
                            <td className="p-3 text-[10px] text-slate-400 leading-tight italic">{row.relevantLaws}</td>
                          </tr>
                        ))
                      )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'library' && (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/50">
             <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wide">Standard Vault</h3>
             <button 
                onClick={() => { handleResetForm(); setActiveTab('master'); }} 
                className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-sm"
             >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                Upload New
             </button>
          </div>
          <table className="w-full text-left text-sm border-collapse">
            <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700 text-slate-500 font-medium uppercase text-xs">
              <tr><th className="p-4">Standard Identifier</th><th className="p-4 text-center">Dataset Size</th><th className="p-4 text-center">Last Updated</th><th className="p-4 text-center">Operations</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {standards.length === 0 ? (
                <tr><td colSpan={4} className="p-12 text-center text-slate-400 italic">Vault is empty.</td></tr>
              ) : (
                standards.map(s => (
                  <tr key={s.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                    <td className="p-4">
                      <div className="font-medium text-slate-900 dark:text-white">{s.standardName}</div>
                      <div className="text-xs text-slate-500">{s.ownerCompany}</div>
                    </td>
                    <td className="p-4 text-center text-slate-600 dark:text-slate-400">
                      <span className="font-mono font-medium">{s.regulatoryRows?.length || 0}</span> Substances
                    </td>
                    <td className="p-4 text-center text-xs text-slate-500 font-mono">{s.effectiveDate}</td>
                    <td className="p-4">
                      <div className="flex justify-center gap-2">
                        <button onClick={() => handleLoadFromLibrary(s)} className="px-3 py-1.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors">Load</button>
                        {deleteConfirmId === s.id ? (
                          <div className="flex gap-1">
                            <button onClick={() => handleDeleteStandard(s.id)} className="px-3 py-1.5 bg-red-600 border border-red-600 rounded text-xs font-medium text-white hover:bg-red-700 transition-colors">Confirm</button>
                            <button onClick={() => setDeleteConfirmId(null)} className="px-3 py-1.5 bg-slate-200 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors">Cancel</button>
                          </div>
                        ) : (
                          <button onClick={() => setDeleteConfirmId(s.id)} className="px-3 py-1.5 bg-white dark:bg-slate-700 border border-red-200 dark:border-red-900/50 rounded text-xs font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">Delete</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'analysis' && (
        <div className="max-w-2xl mx-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-8 rounded-xl shadow-sm space-y-8">
           <div className="text-center">
             <h3 className="text-xl font-bold text-slate-900 dark:text-white">Compliance Audit Deployer</h3>
             <p className="text-sm text-slate-500 mt-1">Select a target drawing and standards to run analysis</p>
           </div>
           
           <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-xs font-medium text-slate-500 uppercase">Target Drawing (Source Data)</label>
                <select className="w-full p-3 border border-slate-200 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-700 text-sm font-medium text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500" value={selectedTarget} onChange={e => setSelectedTarget(e.target.value)}>
                   <option value="">-- Select Drawing --</option>
                   {savedFiles.map(f => <option key={f.dwg.dwg_number} value={f.dwg.dwg_number}>{f.dwg.dwg_number} - {f.dwg.customer}</option>)}
                </select>
                <p className="text-[10px] text-slate-400 italic px-1">
                    * Select from processed drawings in Data Archives. To add new drawings, use the Import Data (Terminal) view.
                </p>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium text-slate-500 uppercase">Regulatory Standard Sets</label>
                <div className="grid gap-2 max-h-[500px] overflow-y-auto pr-2">
                  {standards.map(std => (
                    <div key={std.id} className={`border rounded-lg transition-all ${selectedStandards.includes(std.id) ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-700'}`}>
                      <div className="flex items-center justify-between p-3">
                        <label className="flex items-center gap-3 cursor-pointer flex-1">
                          <input type="checkbox" checked={selectedStandards.includes(std.id)} onChange={e => {
                            if (e.target.checked) {
                                setSelectedStandards([...selectedStandards, std.id]);
                                setExpandedStdId(std.id); // Auto expand on select
                            } else {
                                setSelectedStandards(selectedStandards.filter(id => id !== std.id));
                            }
                          }} className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500" />
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-slate-900 dark:text-white">{std.standardName}</span>
                            <span className="text-xs text-slate-500">{std.regulatoryRows?.length || 0} Substances</span>
                          </div>
                        </label>
                        <button onClick={() => setExpandedStdId(expandedStdId === std.id ? null : std.id)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors">
                           <svg className={`w-4 h-4 transform transition-transform ${expandedStdId === std.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                        </button>
                      </div>
                      
                      {expandedStdId === std.id && (
                        <div className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-b-lg overflow-hidden">
                           <div className="p-2 bg-slate-50 dark:bg-slate-700/50 text-[10px] font-bold text-slate-500 uppercase flex justify-between">
                              <span>Substance Criteria Preview</span>
                              <span>{std.regulatoryRows?.length || 0} Items</span>
                           </div>
                           <div className="max-h-[300px] overflow-y-auto">
                             <table className="w-full text-left text-[10px]">
                               <thead className="bg-slate-50 dark:bg-slate-700/30 text-slate-500 sticky top-0">
                                 <tr>
                                   <th className="p-2 w-8">
                                     <input type="checkbox" 
                                       checked={(selectedRows[std.id]?.length || 0) === (std.regulatoryRows?.length || 0) && (std.regulatoryRows?.length || 0) > 0}
                                       onChange={(e) => {
                                          if (e.target.checked) {
                                              setSelectedRows({...selectedRows, [std.id]: std.regulatoryRows?.map((_, i) => i) || []});
                                          } else {
                                              const newSel = {...selectedRows};
                                              delete newSel[std.id];
                                              setSelectedRows(newSel);
                                          }
                                       }}
                                       className="rounded text-blue-600 focus:ring-blue-500"
                                     />
                                   </th>
                                   <th className="p-2 font-medium">Substance</th>
                                   <th className="p-2 font-medium">CAS No.</th>
                                   <th className="p-2 font-medium">Criteria</th>
                                 </tr>
                               </thead>
                               <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                 {(std.regulatoryRows || []).map((row, idx) => (
                                   <tr key={idx} className={`hover:bg-slate-50 dark:hover:bg-slate-700/20 ${(selectedRows[std.id]?.includes(idx)) ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}>
                                     <td className="p-2">
                                        <input type="checkbox" 
                                          checked={selectedRows[std.id]?.includes(idx) || false}
                                          onChange={(e) => {
                                              const current = selectedRows[std.id] || [];
                                              let updated;
                                              if (e.target.checked) {
                                                  updated = [...current, idx];
                                              } else {
                                                  updated = current.filter(i => i !== idx);
                                              }
                                              setSelectedRows({...selectedRows, [std.id]: updated});
                                          }}
                                          className="rounded text-blue-600 focus:ring-blue-500"
                                        />
                                     </td>
                                     <td className="p-2 text-slate-700 dark:text-slate-300 truncate max-w-[150px]" title={row.substance}>{row.substance}</td>
                                     <td className="p-2 font-mono text-slate-500">{row.casNumber}</td>
                                     <td className="p-2 text-slate-600 dark:text-slate-400 truncate max-w-[150px]" title={row.hondaCriteria}>{row.hondaCriteria}</td>
                                   </tr>
                                 ))}
                               </tbody>
                             </table>
                           </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <button onClick={executeAnalysis} disabled={isAnalyzing || !selectedTarget || selectedStandards.length === 0} className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg shadow-sm hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                {isAnalyzing ? 'Running Analysis...' : 'Start Compliance Check'}
              </button>
           </div>
        </div>
      )}

      {activeTab === 'results' && (
        <div className="space-y-6">
           {results.length > 0 && (
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900 p-6 rounded-xl text-center">
                   <div className="text-xs font-bold text-red-600 uppercase tracking-wider mb-1">Violations Detected</div>
                   <div className="text-4xl font-bold text-red-700 dark:text-red-400">{results.filter(r => r.status === 'EXCEED').length}</div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-900 p-6 rounded-xl text-center">
                   <div className="text-xs font-bold text-green-600 uppercase tracking-wider mb-1">Passed Verification</div>
                   <div className="text-4xl font-bold text-green-700 dark:text-green-400">{results.filter(r => r.status === 'COMPLY').length}</div>
                </div>
             </div>
           )}
           <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm overflow-hidden">
              <table className="w-full text-left text-xs border-collapse">
                <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700 text-slate-500 font-medium uppercase">
                  <tr><th className="p-4">Part Identifier</th><th className="p-4">Audited Substance</th><th className="p-4 text-center">Actual (PPM)</th><th className="p-4 text-center">Limit (PPM)</th><th className="p-4 text-center">Verdict</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {results.length === 0 ? (
                    <tr><td colSpan={5} className="p-12 text-center text-slate-400 italic">No Analysis Logs Found</td></tr>
                  ) : (
                    results.map((res, i) => (
                      <tr 
                        key={i} 
                        onClick={() => onViewDrawing && onViewDrawing(res.dwgReference)}
                        className={`hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer ${res.status === 'EXCEED' ? 'bg-red-50/50 dark:bg-red-900/10' : ''}`}
                      >
                        <td className="p-4">
                          <div className="font-medium text-slate-900 dark:text-white">{res.partNumber}</div>
                          <div className="text-[10px] text-slate-500">{res.dwgReference}</div>
                        </td>
                        <td className="p-4">
                          <div className="font-medium text-blue-600 dark:text-blue-400">{res.substance}</div>
                          <div className="text-[10px] text-slate-400 italic truncate max-w-xs" title={res.regulatoryInfo}>{res.regulatoryInfo}</div>
                        </td>
                        <td className="p-4 text-center font-mono text-slate-700 dark:text-slate-300">{res.actualValue}</td>
                        <td className="p-4 text-center font-mono text-slate-500">{res.limitValue}</td>
                        <td className="p-4 text-center">
                          <span className={`px-2.5 py-1 text-[10px] font-bold rounded-full ${res.status === 'COMPLY' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {res.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
           </div>
        </div>
      )}
    </div>
  );
};

export default ImdsStandardPage;
